import {
  ColumnDef,
  ColumnHelper,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Table,
} from '@tanstack/table-core'
import { BaseWebComponent } from '../components/BaseWebComponent.js'
import { aoiStore } from '../stores/AOIStore.js'
import { FabricFieldData, SelectedField, TableData } from '../utils/types.js'
import { flexRender, useTable } from '../utils/useTable.js'
import { queryElement } from '../utils/utils.js'

const template = `
<style>

  .cqmt-table-positioning {
    height: 50vh;
    display: inline-flex;
    position: absolute;
    bottom: 0;
    left: 4.25rem;
    width: calc(100% - 4.25rem);
    z-index: 20;
  }
  .cqmt-table-container {
    width: calc(100% - 24px);
    padding: 12px 12px 4px;
    background-color: #FFFFFF99;
  }
  .cqmt-table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
  }

  .cqmt-table-info {
    padding: 6px 10px;
    border-radius: 6px;
    background-color: #FFFFFF;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: #6B7280;
  }

  .cqmt-table-count {
    font-weight: 600;
    color: #374151;
  }

  .cqmt-table-label {
    font-weight: 400;
    color: #374151f2;
  }

  button {
    all: unset;
    outline: none;
    background: transparent;
    border: 1px solid transparent;
  }

  .cqmt-data-table-close {
    cursor: pointer;
    padding-right: 16px;
    filter: invert(66%) sepia(2%) saturate(452%) hue-rotate(325deg) brightness(90%) contrast(85%);
  }
  .cqmt-no-data-message {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
    letter-spacing: 0.5%;
    color: #0D1420;
    text-align: center;
    padding-top: 24px;
    color: gray;
  }

  .cqmt-loading-container {
    width: 100%;
    height: 80%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    .cqmt-field-loader {
      border: 10px solid var(--bunker-50);
      border-top: 10px solid var(--patina-700); 
      border-radius: 50%;
      width: 60px;
      height: 60px;
      animation: spin 2s linear infinite;
    }

  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .cqmt-data-table {
    height: calc(100% - 45px);
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 0;
    background-color: #FFFFFF;
    overflow: auto;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);

    table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;

      thead {
        position: sticky;
        top: 0;
        z-index: 10;
        background: #F9FAFB;
        border-bottom: 2px solid #E5E7EB;

        th {
          border-bottom: 1px solid #E5E7EB;
          border-right: 1px solid #F3F4F6;
          padding: 12px 16px;
          text-align: left;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
          line-height: 1.25rem;
          letter-spacing: 0.025em;
          color: #374151;
          cursor: pointer;
          user-select: none;
          position: relative;
          white-space: nowrap;
          background: #F9FAFB;

          .th-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .th-col-name {
            width: calc(100% - 16px);
            text-wrap: nowrap;
            text-overflow: clip;
            overflow-x: hidden;
          }

          &:last-child {
            border-right: none;
          }

          &:hover {
            color: var(--patina-800);
            background: #DDF7E9;
          }

          .sort-indicator {
            display: inline-block;
            margin-left: 8px;
            font-size: 1.5rem;
            color: var(--patina-200);
            font-weight: 400;

            img {
              width: 16px;
              height: 16px;
              display: block;
            }

          }

          &.sorted-asc {
            background: #C4Edd5;

            &:hover {
              background: #B8EACA;
            }
          }

          &.sorted-desc {
            background: #C4Edd5;

            &:hover {
              background: #B8EACA;
            }
          }

        }
      }

      tbody {
        background: #FFFFFF;

        .data-table-row {
          border-bottom: 1px solid #F3F4F6;

          &:hover {
            background: #DDF7E9;
          }

          &:last-child {
            border-bottom: none;
          }

          .data-table-zoom-button {
            height: 18px;
            width: 18px;
            opacity: 0.6;
            transition: opacity 0.1s ease-in-out;

            &:hover {
              cursor: pointer;
              opacity: 1;
            }
          }
        }

        td {
          border-right: 1px solid #F9FAFB;
          padding: 12px 16px;
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 0.875rem;
          line-height: 1.25rem;
          color: #111827;
          vertical-align: middle;

          &:first-child {
            text-align: center;
          }

          &:last-child {
            border-right: none;
          }

          &.sorted-column-cell {
            background: #DDF7E9;
          }

          .data-table-row:hover &.sorted-column-cell {
            background: #C4Edd5;
          }

        }
      }
    }
  }

</style>

<div class="cqmt-table-positioning">
  <div class="cqmt-table-container">
    <div class="cqmt-table-header">
      <div class="cqmt-table-info">
        <span id="tableCount">
          <!-- TODO: Uncomment when filtering results is implemented
          <span class="cqmt-table-label">showing </span>
          <span id="current" class="cqmt-table-count"></span>
          <span class="cqmt-table-label">of</span>
          -->
          <span id="total" class="cqmt-table-count"></span>
          <span class="cqmt-table-label"> results</span>
        </span>
      </div>
      <button class="cqmt-data-table-close-button">
        <img id="tableClose" class="cqmt-data-table-close" src="CQMT/assets/CloseIcon.svg" />
      </button>
    </div>
    <div class="cqmt-data-table">
      <div id="noDataMessage" class="cqmt-no-data-message"></div>
    </div>
  </div>
</div>
`

export type DataTableProps = {
  tableData?: TableData
}

type DataTableElement = HTMLElement & DataTableProps

export class DataTable extends BaseWebComponent implements DataTableElement {
  private _tableData: TableData
  private _subscriptions: (() => void)[]
  private _columnHelper: ColumnHelper<FabricFieldData>
  private _columns: ColumnDef<FabricFieldData, unknown>[]
  private _table: Table<FabricFieldData>
  private _sorting: SortingState
  private _currentData: FabricFieldData[]
  private _hasDispatchedClose: boolean

  protected get template(): string {
    return template
  }

  constructor() {
    super()

    this.attachShadow({ mode: 'open' })
    this.shadowRoot.innerHTML = template

    this._tableData = { columnNames: [], values: [] }
    this._subscriptions = []
    this._sorting = []
    this._currentData = []
    this._hasDispatchedClose = false
    this.handleDataUpdate = this.handleDataUpdate.bind(this)

    this._columnHelper = createColumnHelper<FabricFieldData>()
  }

  getFieldName(field: SelectedField): string {
    let result = field.fieldname as string
    result = aoiStore
      .getState()
      .reportingFields.find((f) => f.layername === field.layername)
      .fields.find((f) => f.fieldname === field.fieldname).displayname
    return result
  }

  getFieldDescription(field: SelectedField): string {
    let result = ''
    result = aoiStore
      .getState()
      .reportingFields.find((f) => f.layername === field.layername)
      .fields.find((f) => f.fieldname === field.fieldname).shortdescription
    return result
  }

  formatCellValue(field: SelectedField, rawValue: string | number): string {
    if (rawValue === null || rawValue === undefined) return 'N/A'

    const sanitizedValue =
      typeof rawValue === 'number' ? String(rawValue) : rawValue

    const reportingField = aoiStore
      .getState()
      .reportingFields.find((f) => f.layername === field.layername)
      .fields.find((f) => f.fieldname === field.fieldname)

    if (
      reportingField.categories &&
      reportingField.categories.length > 0 &&
      rawValue !== null &&
      rawValue !== undefined
    ) {
      const foundCategory = reportingField.categories.find(
        (c) => c.value === rawValue.toString(),
      )
      if (foundCategory) {
        return `<span title="${foundCategory.definition}">${foundCategory?.category}</span>`
      }
    }

    switch (reportingField.unitofmeasure) {
      case 'Percentage':
      case 'Probability':
      case 'Probabilty':
        return (parseFloat(sanitizedValue) * 100).toFixed(4) + '%'
      case 'US Dollars':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(parseFloat(sanitizedValue))
      case 'Count':
      case 'Household Members':
        return new Intl.NumberFormat('en-US').format(parseInt(sanitizedValue))
      case 'Decimal degrees (WGS84)':
      case 'Score':
      case 'Real Number':
        return parseFloat(sanitizedValue).toFixed(6)
      case 'Feet':
        return parseFloat(sanitizedValue).toFixed(2) + ' ft'
      case 'Degrees':
        return parseFloat(sanitizedValue).toFixed(2) + '°'
      case 'Days':
        return (
          parseInt(sanitizedValue) +
          (parseInt(sanitizedValue) === 1 ? ' day' : ' days')
        )
      case 'Events':
        return (
          parseInt(sanitizedValue) +
          (parseInt(sanitizedValue) === 1 ? ' event' : ' events')
        )
      case 'Length Miles':
        return parseFloat(sanitizedValue).toFixed(2) + ' miles'
      case 'Area in Square Miles':
        return parseFloat(sanitizedValue).toFixed(2) + ' sq mi'
      case 'Fabric Locations per Square Mile':
        return parseFloat(sanitizedValue).toFixed(2) + ' loc/sq mi'
      case 'Fabric Locations per Road Mile':
        return parseFloat(sanitizedValue).toFixed(2) + ' loc/road mi'
      case 'True/False':
        return sanitizedValue ? 'True' : 'False'
      case 'Federal Information Processing Standard':
      case 'Unique Key':
        return sanitizedValue.toString()
      default:
        return rawValue.toString()
    }
  }

  /**
   * Handles data updates from the AOI store and rebuilds the table with sorting capabilities
   * @param data - Array of fabric field data to display in the table
   */
  async handleDataUpdate(data: FabricFieldData[]) {
    this._currentData = data
    this._columns = [
      this._columnHelper.display({
        id: 'zoom',
        header: () => '',
        cell: (info) => {
          const { uuid } = info.row.original
          return `<img class="data-table-zoom-button" src="CQMT/assets/ZoomIcon.svg" data-uuid="${uuid}" />`
        },
        enableSorting: false,
        size: 10,
      }),
      this._columnHelper.accessor('location_id', {
        header: () => 'Location ID',
        cell: (info) => info.renderValue(),
        enableSorting: true,
      }),
      ...aoiStore.getState().selectedFields.map((field) => {
        return this._columnHelper.accessor(field.fieldname, {
          header: () =>
            `<div title="${this.getFieldName(field)}">${this.getFieldName(field)}</div>`,
          cell: (info) => {
            return this.formatCellValue(
              field,
              info.row.original[info.column.id],
            )
          },
          enableSorting: true,
        })
      }),
    ]

    this.rebuildTable()
    this.renderTable()
    this.renderTableData()

    this.tableData = {
      columnNames: [
        'uuid',
        'Location ID',
        ...aoiStore.getState().selectedFields.map((f) => f.fieldname),
      ],
      values: data.map((row) => [
        row.uuid,
        row.location_id,
        ...aoiStore
          .getState()
          .selectedFields.map(
            (field) => row[field.fieldname.replace(/"/g, '')],
          ),
      ]),
    }
  }

  /**
   * Rebuilds the table with current data and sorting state
   */
  private rebuildTable() {
    this._table = useTable<FabricFieldData>({
      data: this._currentData,
      columns: this._columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      enableSortingRemoval: true,
      state: {
        sorting: this._sorting,
      },
      onSortingChange: (updater) => {
        if (typeof updater === 'function') {
          this._sorting = updater(this._sorting)
        } else {
          this._sorting = updater
        }
        this.rebuildTable()
        this.renderTable()
      },
    })
  }

  /**
   * Handles loading states for UUID and data fetching
   */
  handleUuidLoading() {
    const { isUUIDLoading, isDataLoading } = aoiStore.getState()
    const isLoading = isUUIDLoading || isDataLoading

    const tableContainer = queryElement(
      this.shadowRoot,
      HTMLElement,
      '.cqmt-data-table',
    )
    const table = this.shadowRoot.querySelector('table') as HTMLElement
    const noDataMessage = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#noDataMessage',
    )

    let loadingContainer = this.shadowRoot.querySelector(
      '.cqmt-loading-container',
    ) as HTMLElement

    if (isLoading) {
      if (table) table.style.display = 'none'
      noDataMessage.style.display = 'none'

      // Show loading container
      if (!loadingContainer) {
        loadingContainer = document.createElement('div')
        loadingContainer.className = 'cqmt-loading-container'
        const fieldLoader = document.createElement('div')
        fieldLoader.className = 'cqmt-field-loader'
        loadingContainer.appendChild(fieldLoader)
        tableContainer.appendChild(loadingContainer)
      }
    } else {
      loadingContainer?.remove()
      this.renderTableData()
    }
  }

  async connectedCallback() {
    const unsubscribeFromDataUpdates = aoiStore.subscribe(
      (state) => state.data,
      (data) => this.handleDataUpdate(data),
    )
    this._subscriptions.push(unsubscribeFromDataUpdates)

    const unsubscribeFromUuidLoadingUpdates = aoiStore.subscribe(
      (state) => state.isUUIDLoading,
      () => this.handleUuidLoading(),
    )
    this._subscriptions.push(unsubscribeFromUuidLoadingUpdates)

    const unsubscribeFromDataLoadingUpdates = aoiStore.subscribe(
      (state) => state.isDataLoading,
      () => this.handleUuidLoading(),
    )
    this._subscriptions.push(unsubscribeFromDataLoadingUpdates)

    this.shadowRoot
      .querySelector('#tableClose')
      .addEventListener('click', this.handleCloseClick)

    // Initial render
    await this.handleDataUpdate(aoiStore.getState().data)
  }

  disconnectedCallback() {
    this._subscriptions.forEach((unsubscribe) => unsubscribe())
    this.dispatchCloseEvent()

    const table = this.shadowRoot.querySelector('table')
    if (table) {
      table.removeEventListener('click', this.handleZoomIconClick)
    }
  }

  get tableData() {
    return this._tableData
  }
  set tableData(newVal) {
    this._tableData = newVal
    this.renderTableData()
    this.renderTable()
  }

  /**
   * Gets the sort indicator symbol based on the current sort state
   * @param columnId - The ID of the column to get the indicator for
   * @returns The appropriate sort indicator symbol
   */
  private getSortIndicator(columnId: string): string {
    const sortState = this._sorting.find((sort) => sort.id === columnId)
    if (!sortState) return '<img src="CQMT/assets/SortIcon.svg" />'

    return sortState.desc
      ? '<img src="CQMT/assets/SortDownIcon.svg" />'
      : '<img src="CQMT/assets/SortUpIcon.svg" />'
  }

  /**
   * Gets the CSS class for the sort state of a column
   * @param columnId - The ID of the column to get the class for
   * @returns The appropriate CSS class name
   */
  private getSortClass(columnId: string): string {
    const sortState = this._sorting.find((sort) => sort.id === columnId)
    if (!sortState) return ''
    return sortState.desc ? 'sorted-desc' : 'sorted-asc'
  }

  /**
   * Handles custom three-state sorting (asc → desc → none)
   * @param columnId - The ID of the column to sort
   */
  private handleColumnSort(columnId: string) {
    const currentSort = this._sorting.find((sort) => sort.id === columnId)

    if (!currentSort) {
      this._sorting = [{ id: columnId, desc: false }]
    } else if (!currentSort.desc) {
      this._sorting = [{ id: columnId, desc: true }]
    } else {
      this._sorting = []
    }

    this.rebuildTable()
    this.renderTable()
  }

  renderTable = () => {
    const existingTable = this.shadowRoot.querySelector('table')
    if (existingTable) {
      existingTable.remove()
    }

    const tableElement = document.createElement('table')
    const theadElement = document.createElement('thead')
    const tbodyElement = document.createElement('tbody')

    tableElement.appendChild(theadElement)
    tableElement.appendChild(tbodyElement)

    const currentlySortedColumnId =
      this._sorting.length > 0 ? this._sorting[0].id : null

    this._table.getHeaderGroups().forEach((headerGroup) => {
      const trElement = document.createElement('tr')
      headerGroup.headers.forEach((header) => {
        const thElement = document.createElement('th')
        const columnSize = header.getSize()
        if (columnSize) {
          thElement.style.width = `${columnSize}px`
          thElement.style.minWidth = `${columnSize}px`
          thElement.style.maxWidth = `${columnSize}px`
        }

        // Add sorting elements and click listeners
        thElement.className = this.getSortClass(header.column.id)
        let sortIndicator: HTMLSpanElement | null = null
        if (header.column.getCanSort()) {
          sortIndicator = document.createElement('span')
          sortIndicator.className = 'sort-indicator'
          sortIndicator.innerHTML = this.getSortIndicator(header.column.id)
        }
        if (header.column.getCanSort()) {
          thElement.addEventListener('click', () => {
            this.handleColumnSort(header.column.id)
          })
        }

        const thContent = document.createElement('div')
        thContent.className = 'th-content'

        const headerContent = document.createElement('span')
        headerContent.className = 'th-col-name'
        headerContent.innerHTML = flexRender(
          header.column.columnDef.header,
          header.getContext(),
        )

        thContent.appendChild(headerContent)
        if (sortIndicator) {
          thContent.appendChild(sortIndicator)
        }
        thElement.appendChild(thContent)

        trElement.appendChild(thElement)
      })
      theadElement.appendChild(trElement)
    })

    this._table.getRowModel().rows.forEach((row) => {
      const trElement = document.createElement('tr')
      trElement.classList.add('data-table-row')
      row.getVisibleCells().forEach((cell) => {
        const tdElement = document.createElement('td')
        const columnSize = cell.column.getSize()
        if (columnSize) {
          tdElement.style.width = `${columnSize}px`
          tdElement.style.minWidth = `${columnSize}px`
          tdElement.style.maxWidth = `${columnSize}px`
        }

        if (
          currentlySortedColumnId &&
          cell.column.id === currentlySortedColumnId
        ) {
          tdElement.classList.add('sorted-column-cell')
        }

        tdElement.innerHTML = flexRender(
          cell.column.columnDef.cell,
          cell.getContext(),
        )
        trElement.appendChild(tdElement)
      })
      tbodyElement.appendChild(trElement)
    })

    tableElement.addEventListener('click', this.handleZoomIconClick)

    const wrapperElement = queryElement(
      this.shadowRoot,
      HTMLElement,
      '.cqmt-data-table',
    )
    wrapperElement.appendChild(tableElement)
  }

  /**
   * Handles zoom icon clicks and dispatches custom event
   * @param event - The click event from the zoom icon
   */
  private handleZoomIconClick = (event: Event) => {
    const target = event.target as HTMLElement
    if (target.classList.contains('data-table-zoom-button')) {
      const uuid = target.getAttribute('data-uuid')
      if (uuid) {
        const zoomToEvent = new CustomEvent('cqmt-zoom-to-uuid', {
          bubbles: true,
          cancelable: true,
          composed: true,
          detail: { uuid },
        })
        this.dispatchEvent(zoomToEvent)
      }
    }
  }

  private handleCloseClick = () => {
    this.dispatchCloseEvent()
    this.remove()
  }

  private dispatchCloseEvent() {
    if (this._hasDispatchedClose) return

    const closeEvent = new CustomEvent('cqmt-data-table-close', {
      bubbles: true,
      composed: true,
    })
    this.dispatchEvent(closeEvent)
    this._hasDispatchedClose = true
  }

  renderTableData() {
    const tableCount = queryElement(this.shadowRoot, HTMLElement, '#tableCount')
    const table = this.shadowRoot.querySelector('table') as HTMLElement
    const noDataMessage = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#noDataMessage',
    )

    // TODO: Uncomment when filtering results is implemented
    // const currentNumOfResultsElement = queryElement(
    //   this.shadowRoot,
    //   HTMLElement,
    //   '#current',
    // )

    const totalNumOfResultsElement = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#total',
    )

    // Check for loading states first
    const { isUUIDLoading, isDataLoading } = aoiStore.getState()
    if (isUUIDLoading || isDataLoading) {
      tableCount.style.display = 'none'
      if (table) table.style.display = 'none'
      noDataMessage.style.display = 'none'
      return
    }

    if (!aoiStore.getState().areaOfInterest) {
      tableCount.style.display = 'block'
      if (table) table.style.display = 'none'
      noDataMessage.innerText = 'Select an Area of Interest to view data'
      noDataMessage.style.display = 'block'

      // TODO: Uncomment when filtering results is implemented
      // currentNumOfResultsElement.innerText = '0'
      totalNumOfResultsElement.innerText = '0'
      return
    }

    if (this._currentData.length === 0) {
      tableCount.style.display = 'block'
      if (table) table.style.display = 'none'
      noDataMessage.innerText =
        'No data available for the selected Area of Interest'
      noDataMessage.style.display = 'block'

      // TODO: Uncomment when filtering results is implemented
      // currentNumOfResultsElement.innerText = '0'
      totalNumOfResultsElement.innerText = '0'
      return
    }

    tableCount.style.display = 'block'
    if (table) table.style.display = 'table'
    noDataMessage.style.display = 'none'
    noDataMessage.innerText = ''

    // TODO: Uncomment when filtering results is implemented
    // currentNumOfResultsElement.innerText = this._currentData.length.toString()
    totalNumOfResultsElement.innerText = this._currentData.length.toString()
  }
}

if (!customElements.get('cqmt-data-table')) {
  window.customElements.define('cqmt-data-table', DataTable)
}
